import React from "react";
import { FaFileExcel, FaFileCsv } from "react-icons/fa";
import templateFile from "../../../assets/templates/job_questions_template.xlsx";
import { toastNotification } from "../../../services/notification-service";

const FileUploadSection = ({ data, setData, parseFunction }) => {
  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
  const VALID_FILE_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
    "application/csv"
  ];

  const handleFileValidation = (file) => {
    if (!VALID_FILE_TYPES.includes(file.type)) {
      toastNotification({
        status: "error",
        message: "Only XLSX and CSV files are allowed",
      });
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toastNotification({
        status: "error",
        message: "File size must be less than 2MB",
      });
      return false;
    }

    return true;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !parseFunction) return;
  
    if (!handleFileValidation(file)) return;
  
    try {
      const result = await parseFunction(file);
      setData((prevData) => ({
        ...prevData,
        passage_questions: result?.passage_questions || [],
        additional_questions: result?.additional_questions || [],
        fileName: file.name,
        fileSize: `${(file.size / 1024).toFixed(2)} KB`,
      }));
    } catch (error) {
      let errorMessage = "Failed to process file. Please try again.";
  
      if (error?.message?.includes("Missing required headers")) {
        errorMessage = error.message;
      } else if (error?.message?.includes("empty")) {
        errorMessage = "The uploaded file is empty or has no data.";
      }
  
      toastNotification({
        status: "error",
        message: errorMessage,
      });
  
      console.error("File processing error:", error);
    }
  };
  

  const DownloadTemplateButton = () => (
    <a
      href={templateFile}
      download="job_questions_template.xlsx"
      className="text-sm text-blue-600 hover:underline flex items-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        className="w-4 h-4 mr-1"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
        />
      </svg>
      Download Excel Template
    </a>
  );

  const UploadButton = () => (
    <label className="text-sm text-blue-600 hover:underline flex items-center cursor-pointer">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        className="w-4 h-4 mr-1"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
        />
      </svg>
      Upload Questions
      <input
        type="file"
        className="hidden"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileUpload}
      />
    </label>
  );

  const FilePreview = () => {
    if (!data?.fileName) return null;
    
    const isExcel = data.fileName.endsWith('.xlsx') || data.fileName.endsWith('.xls');
    const Icon = isExcel 
      ? <FaFileExcel className="w-6 h-6 mr-2 text-green-600" />
      : <FaFileCsv className="w-6 h-6 mr-2 text-orange-500" />;

    return (
      <div className="flex items-center mt-2 p-2 border border-gray-200 rounded-md bg-gray-50">
        {Icon}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {data.fileName}
          </p>
          <p className="text-xs text-gray-500">
            {data.fileSize}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-wrap gap-4 mb-4 ml-4">
      <DownloadTemplateButton />
      <UploadButton />
      <FilePreview />
    </div>
  );
};

export default FileUploadSection;